/**
 * BacktestSidebar — read-only single-list design.
 *
 * SHA-77: the "+ New Backtest" launch button + NewBacktestPicker were removed —
 * MCP is now the sole surface for starting new backtests. This sidebar only
 * lists and opens past runs.
 *
 * 1. Error banner (from backtestStore)
 * 2. ONE "Recent runs" header above the scroll container
 * 3. Unified single sorted list — infinite-scroll paginated (30/page)
 *    - Sort key per run: openedAt (epoch ms, from openedRunsStore) if the run
 *      was ever opened via Explore Runs, otherwise Date.parse(run.runAt).
 *      Descending → most-recently-opened floats to top; rest by creation date.
 *    - Merges opened-store runs (which may not be in the loaded pages) with
 *      the paginated runs; deduplicates by id.
 *    - Falls back to pinning the currentResult run when it isn't in any source.
 *    - Highlights the globally-viewed run (from backtestStore).
 *    - Auto-scrolls to the selected run whenever the selection changes.
 *
 * markOpened is called ONLY from App.tsx onPickRun (Explore Runs) — NOT here.
 * In-sidebar clicks move the highlight but do NOT reorder the list.
 *
 * RULE 13: no compute here — run initiation delegates to the parent callbacks.
 */

import { useCallback, useRef, useEffect, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getHistory } from '../../api/client';
import type { BacktestSummary, ApiBacktestResult, AggregateBacktestResult } from '../../types';
import { useBacktestStore } from '../../stores/backtestStore';
import { useScrollToSelected } from '../../hooks/useScrollToSelected';
import { useOpenedRunsStore } from '../../stores/openedRunsStore';
import { Spinner } from '../Spinner/Spinner';
import { RouteLink } from '../RouteLink/RouteLink';
import { isAggregationRun, getCompositionLabel } from '../../utils/runComposition';

// ── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 30;

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeTime(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay > 0) return `${diffDay}d`;
  if (diffHr > 0) return `${diffHr}h`;
  if (diffMin > 0) return `${diffMin}m`;
  return 'now';
}

function formatReturn(val: number | null | undefined): string {
  if (val == null) return '—';
  const prefix = val >= 0 ? '+' : '';
  return `${prefix}${val.toFixed(1)}%`;
}

function formatSharpe(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Sh ${val.toFixed(2)}`;
}

/**
 * SHA-64: color the sidebar Sharpe value by sign, mirroring the return
 * coloring above. Convention: > 0 -> green (sl-pos), < 0 -> red (sl-neg),
 * 0 / null / NaN -> neutral (sl-dim) — never red for missing or zero data.
 */
function sharpeClass(val: number | null | undefined): string {
  if (val == null || Number.isNaN(val) || val === 0) return 'sl-dim';
  return val > 0 ? 'sl-pos' : 'sl-neg';
}

/**
 * Build a lightweight BacktestSummary from a full result stored in the backtest
 * store. Used to add the selected run to the list when it isn't in the loaded
 * pages or the opened-runs store.
 */
function resultToSummary(
  result: ApiBacktestResult | AggregateBacktestResult
): BacktestSummary {
  const aggResult = result as AggregateBacktestResult;
  return {
    id: result.id,
    strategyName: result.config.strategyName,
    symbol: result.config.symbol,
    timeframe: result.config.timeframe,
    totalReturnPercent: result.metrics.totalReturnPercent,
    sharpeRatio: result.metrics.sharpeRatio,
    runAt: new Date(result.createdAt).toISOString(),
    aggregationId: result.aggregationId ?? aggResult.aggregationId,
    aggregationName: aggResult.aggregationName,
    strategyConfigId: result.strategyConfigId,
    maxDrawdownPercent: result.metrics.maxDrawdownPercent,
    winRate: result.metrics.winRate,
    profitFactor: result.metrics.profitFactor,
    totalTrades: result.metrics.totalTrades,
    totalFees: result.metrics.totalFees,
    exchange: result.config.exchange,
  };
}

// ── Composition badge ────────────────────────────────────────────────────────

function CompositionBadge({ run }: { run: BacktestSummary }) {
  // A run is single IFF it has a strategy_config_id (matches server bucketing).
  // Everything else — genuine aggregations AND legacy symbol='MULTI' runs with no
  // config link — is a portfolio, UNLESS it's an aggregation-of-one (exactly one
  // sub-strategy), which is shown with the "1 asset" label instead of "Portfolio".
  const isAgg = isAggregationRun(run);
  const label = getCompositionLabel(run);

  if (isAgg) {
    return (
      <span className="sl-badge sl-badge-multi" style={{ fontSize: 10 }}>
        {label}
      </span>
    );
  }

  return (
    <span className="sl-badge sl-badge-sym" style={{ fontSize: 10 }}>
      {label}
    </span>
  );
}

// ── Single sidebar run row ───────────────────────────────────────────────────

function SideRunRow({
  run,
  isSelected,
  isLoading,
  onSelect,
}: {
  run: BacktestSummary;
  isSelected: boolean;
  isLoading: boolean;
  onSelect: (run: BacktestSummary) => void;
}) {
  const ret = run.totalReturnPercent ?? null;
  const retClass = ret == null ? 'sl-dim' : ret >= 0 ? 'sl-pos' : 'sl-neg';
  const isAgg = isAggregationRun(run);
  const displayName = isAgg ? (run.aggregationName ?? run.strategyName) : run.strategyName;

  return (
    <RouteLink
      data-item-id={run.id}
      href={`/backtesting/${encodeURIComponent(run.id)}`}
      onNavigate={() => onSelect(run)}
      className={`sl-side-run${isSelected ? ' sl-sel' : ''}`}
      title={displayName}
    >
      {/* Line 1: name + ago */}
      <div className="sl-l1">
        <span className="sl-nm">{displayName}</span>
        <span className="sl-ago">{formatRelativeTime(run.runAt)}</span>
      </div>
      {/* Line 2: badge + spacer + return + Sharpe */}
      <div className="sl-l2">
        <CompositionBadge run={run} />
        <span className="sl-sp" />
        {isLoading ? (
          <Spinner size="sm" />
        ) : (
          <>
            <span className={`sl-m ${retClass}`}>{formatReturn(ret)}</span>
            <span className={`sl-m ${sharpeClass(run.sharpeRatio)}`}>{formatSharpe(run.sharpeRatio)}</span>
          </>
        )}
      </div>
    </RouteLink>
  );
}

// ── Main sidebar ─────────────────────────────────────────────────────────────

interface BacktestSidebarProps {
  selectedBacktestId: string | null;
  onSelectRun: (run: BacktestSummary) => void;
}

export function BacktestSidebar({ selectedBacktestId, onSelectRun }: BacktestSidebarProps) {
  const { error, currentResult } = useBacktestStore();
  const storeSelectedId = useBacktestStore((s) => s.selectedBacktestId);

  // Opened-runs store — only the entries array is needed here.
  // markOpened is called only from App.tsx onPickRun (Explore Runs).
  const openedEntries = useOpenedRunsStore((s) => s.opened);

  // Canonical selected id — prop and store should be in sync (App.tsx passes the
  // store value as prop), but use store as tie-breaker to stay in sync even if
  // a parent hasn't yet propagated a HistoryExplorer selection.
  const effectiveSelectedId = selectedBacktestId ?? storeSelectedId;

  const [loadingRunId, setLoadingRunId] = useState<string | null>(null);

  // ── Paginated runs (infinite scroll) ──────────────────────────────────────

  const {
    data: infiniteData,
    isLoading: runsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<
    import('../../types').PaginatedHistory,
    Error
  >({
    queryKey: ['explorer-history', 'sidebar-infinite'],
    queryFn: ({ pageParam = 0 }) =>
      getHistory({
        limit: PAGE_SIZE,
        offset: pageParam as number,
        sortBy: 'runAt',
        sortDir: 'desc',
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) return undefined;
      const totalLoaded = allPages.reduce((sum, page) => sum + page.results.length, 0);
      return totalLoaded;
    },
    staleTime: 20_000,
  });

  const loadedRuns = infiniteData?.pages.flatMap((p) => p.results) ?? [];

  // ── Merge + sort into a single list ───────────────────────────────────────
  //
  // Sort key per run:
  //   openedAt (epoch ms) from the store  →  if the run was opened via Explore Runs
  //   Date.parse(run.runAt)               →  otherwise (creation time)
  // Descending → most-recently-opened floats to the top; rest sorted by creation date.

  const openedAtMap = new Map<string, number>(
    openedEntries.map((e) => [e.run.id, e.openedAt])
  );

  // Build a deduplicated map: loadedRuns first (freshest data), then any
  // opened-store entries that aren't in the loaded pages.
  const mergedMap = new Map<string, BacktestSummary>();
  for (const run of loadedRuns) {
    mergedMap.set(run.id, run);
  }
  for (const entry of openedEntries) {
    if (!mergedMap.has(entry.run.id)) {
      mergedMap.set(entry.run.id, entry.run);
    }
  }

  // Fallback: selected run not in any source yet (deep-link / data still loading).
  if (
    effectiveSelectedId &&
    !mergedMap.has(effectiveSelectedId) &&
    currentResult &&
    currentResult.id === effectiveSelectedId
  ) {
    const fallback = resultToSummary(currentResult);
    mergedMap.set(fallback.id, fallback);
  }

  const sortedRuns = [...mergedMap.values()].sort((a, b) => {
    const keyA = openedAtMap.get(a.id) ?? Date.parse(a.runAt);
    const keyB = openedAtMap.get(b.id) ?? Date.parse(b.runAt);
    return keyB - keyA;
  });

  // ── Scroll to selected ────────────────────────────────────────────────────

  const listRef = useRef<HTMLDivElement>(null);
  // Fires when effectiveSelectedId changes (in-sidebar click / HistoryExplorer), AND
  // retries whenever sortedRuns.length changes — this closes the data race where a
  // deep-linked selection is set before the row for it has loaded (paginated fetch,
  // opened-runs hydration, or the currentResult fallback below all change this length
  // once their data arrives, re-firing the scroll attempt).
  useScrollToSelected(effectiveSelectedId, listRef, [sortedRuns.length]);

  // ── IntersectionObserver sentinel for infinite scroll ─────────────────────

  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // ── Run selection (in-sidebar click) ─────────────────────────────────────
  // Does NOT call markOpened — clicking a run in the list moves the highlight
  // but does NOT reorder the list. markOpened lives only in App.tsx onPickRun.

  const handleSelectRun = useCallback(async (run: BacktestSummary) => {
    setLoadingRunId(run.id);
    try {
      await onSelectRun(run);
    } finally {
      setLoadingRunId(null);
    }
  }, [onSelectRun]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '14px 12px 40px', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
      {/* Error banner */}
      {error && (
        <div className="sl-error">{error}</div>
      )}

      {/* ONE "Recent runs" header — above the scroll container, never scrolls away */}
      <div className="sl-grp">
        Recent runs
        <span className="sl-line" />
      </div>

      {/* Scrollable single list */}
      <div
        ref={listRef}
        className="sl-side-runs"
        style={{ flex: 1, overflow: 'hidden auto' }}
      >
        {runsLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 24 }}>
            <Spinner />
          </div>
        ) : sortedRuns.length === 0 ? (
          <div className="sl-empty">
            No runs yet. Run a backtest via MCP to see it here.
          </div>
        ) : (
          <>
            {sortedRuns.map((run) => (
              <SideRunRow
                key={run.id}
                run={run}
                isSelected={run.id === effectiveSelectedId}
                isLoading={run.id === loadingRunId}
                onSelect={handleSelectRun}
              />
            ))}

            {/* Infinite scroll sentinel */}
            {hasNextPage && (
              <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />
            )}

            {/* Loading more indicator */}
            {isFetchingNextPage && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                <Spinner size="sm" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
